import React, { ReactNode } from 'react';
import { useFriendlyLoadingState } from '../hooks/useFriendlyLoadingState';
import { Loader } from './Loader';

type CommonProps = {
  loading: boolean;
  grace?: number;
  dwell?: number;
  loaderClassName?: string;
  debug?: boolean; // if true, will stay in loading state indefinitely
  'data-testid'?: string;
};

// Discriminated union based on presence of data prop
type FriendlyLoaderProps<T> = CommonProps & (
  | {
      children?: ReactNode;
      data?: never;
    }
  | {
      data: T;
      children: (data: NonNullable<T>) => ReactNode;
    }
);

/**
 * A loading wrapper that provides user-friendly loading states:
 * - Shows nothing initially (idle)
 * - Shows spinner after grace period (loading)
 * - Shows children when loaded, respecting dwell time
 * 
 * Supports two patterns:
 * 1. No children: <FriendlyLoader loading={loading} /> 
 *   - Even if a page is managing it's own rendering of loaded content, it can use this pattern to prevent flickering.
 * 2. Regular children: <FriendlyLoader loading={loading}>{children}</FriendlyLoader>
 * 3. Data-aware: <FriendlyLoader loading={loading} data={data}>{(data) => children}</FriendlyLoader>
 *   - Recommended. Children will be typed as non-null when loaded.
 */
function FriendlyLoader<T>(props: FriendlyLoaderProps<T>): React.ReactElement | null {
  const { loading, data, children, grace, dwell, loaderClassName, debug = false, 'data-testid': testId } = props;

  // Runtime validation for JavaScript usage
  if (process.env.NODE_ENV !== 'production') {
    if (data !== undefined && typeof children !== 'function') {
      console.error(
        'FriendlyLoader: When `data` prop is provided, `children` must be a function that receives the data. ' +
        'Usage: <FriendlyLoader data={data}>{(data) => <YourComponent />}</FriendlyLoader>'
      );
    }
    
    if (data === undefined && typeof children === 'function') {
      console.error(
        'FriendlyLoader: When `data` prop is not provided, `children` should be ReactNode, not a function. ' +
        'Usage: <FriendlyLoader>{<YourComponent />}</FriendlyLoader> or provide the `data` prop.'
      );
    }
  }

  const state = useFriendlyLoadingState(loading, { grace, dwell });
  const loader = <Loader className={loaderClassName} data-testid={testId}/>;

  if (debug) {
    return loader;
  }

  switch (state) {
    case 'idle':
      return null;
    case 'loading':
      return loader;
    case 'loaded':
      // TypeScript can discriminate based on data prop presence
      if (data !== undefined) {
        // We know children is a function here
        const renderFunction = children as (data: NonNullable<T>) => ReactNode;
        const view = renderFunction(data as NonNullable<T>);
        return <div style={{ display: 'contents' }}>{view}</div>;
      }
      // We know children is ReactNode here
      const regularChildren = children as ReactNode;
      return <div style={{ display: 'contents' }}>{regularChildren}</div>;
    default:
      return null;
  }
}

export default FriendlyLoader; 